import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { PageHero, CTABand, Rise, Eyebrow, ArrowCard, FAQAccordion, Reveal } from "@/components/chrome";
import { REGIONS, findRegion } from "@/data/regions";
import { PROVINCES } from "@/data/provinces";
import { SERVICES } from "@/data/services";
import { SITE } from "@/data/site";
import { findProvince, findCity, paths } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

const citiesOf = (region) =>
  region.cities.map((slug) => findCity(region.province, slug)).filter(Boolean);

export function RegionsIndex() {
  const byProvince = PROVINCES.map((p) => ({
    province: p,
    regions: REGIONS.filter((r) => r.province === p.slug),
  })).filter((g) => g.regions.length > 0);

  return (
    <div data-testid="regions-index-page">
      <PageHero
        prefix="regions-index"
        crumbs={[{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }, { name: "Regions" }]}
        eyebrow="Regional service hubs"
        title="Cleaning services by region, coast to coast"
        lead="Beyond individual cities, Tbay Cleaning Services organizes crews around regional hubs — from Northern Manitoba to Metro Vancouver — so even smaller communities get the same insured, audited service."
      />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <div className="space-y-14">
          {byProvince.map(({ province, regions }) => (
            <div key={province.slug}>
              <Link to={paths.province(province.slug)} data-testid={`regions-province-${province.slug}`}
                className="font-display text-xl font-semibold tracking-tight text-slate-900 transition-colors hover:text-cyan-700">
                {province.name}
              </Link>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {regions.map((r) => {
                  const cities = citiesOf(r);
                  return (
                    <Link key={r.slug} to={`/regions/${r.slug}`} data-testid={`region-card-${r.slug}`}
                      className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40 hover:shadow-[0_16px_40px_-16px_rgba(2,132,199,0.25)]">
                      <div className="flex items-center justify-between">
                        <MapPin className="h-4 w-4 text-cyan-700" />
                        <ArrowRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-cyan-600" />
                      </div>
                      <h2 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-900">{r.name}</h2>
                      <p className="mt-1.5 text-sm text-slate-500">
                        {cities.length} {cities.length === 1 ? "community" : "communities"} — {cities.slice(0, 3).map((c) => c.name).join(", ")}{cities.length > 3 ? "…" : ""}
                      </p>
                      <p className="mt-3 text-sm leading-relaxed text-slate-600">{r.blurb}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
      <CTABand lead="One national number reaches every regional crew. Most quotes arrive within one business hour." prefix="regions-cta" />
    </div>
  );
}

export function RegionPage() {
  const { regionSlug } = useParams();
  const region = findRegion(regionSlug);
  const province = region && findProvince(region.province);
  if (!region || !province) return <NotFound />;

  const cities = citiesOf(region);
  const names = cities.map((c) => c.name);
  const lead = cities[0];
  const siblings = REGIONS.filter((r) => r.province === region.province && r.slug !== region.slug);

  const faqs = [
    { q: `What areas does the ${region.name} service region cover?`,
      a: `Our ${region.name} coverage includes ${names.join(", ")} plus surrounding rural communities across ${province.name}. If you're between communities, call ${SITE.phone} — we likely still serve you.` },
    { q: `Do you charge travel fees in ${region.name}?`,
      a: `No travel surcharges within the core ${region.name} service area. Remote call-outs are quoted openly in advance — the flat rate we quote is the price you pay.` },
    { q: `Which cleaning services are available in ${region.name}?`,
      a: `All 12: house cleaning, deep cleaning, maid service, move-in/out cleaning, commercial cleaning, janitorial services, carpet cleaning, window cleaning, post-construction cleaning, Airbnb turnovers, eco-friendly cleaning, and pressure washing.` },
    { q: `How do I book cleaning in ${region.name}?`,
      a: `Call ${SITE.phone} or email ${SITE.email}. A real coordinator confirms your ${region.name} appointment with a firm flat-rate quote — usually within one business hour.` },
  ];

  return (
    <div data-testid="region-page">
      <PageHero prefix="region"
        crumbs={[{ name: "Home", path: "/" }, { name: "Locations", path: "/locations" }, { name: "Regions", path: "/regions" }, { name: region.name }]}
        eyebrow={`${province.name} — ${cities.length} ${cities.length === 1 ? "community" : "communities"}`}
        title={`Cleaning Services in ${region.name}`}
        lead={region.blurb} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="region-overview">
        <div className="max-w-3xl space-y-5">
          <p className="leading-relaxed text-slate-600 sm:text-lg">
            Tbay Cleaning Services provides professional cleaning across {region.name}
            {region.name.includes(province.name) ? "" : `, ${province.name}`} — serving{" "}
            {names.join(", ")} and surrounding communities with 12 specialized services for homes, offices, rentals, and job sites.
          </p>
          <p className="leading-relaxed text-slate-600 sm:text-lg">
            {province.blurb} In {region.name}, that means crews who know the local housing stock, the seasonal
            realities, and the drive times between communities — with flat-rate quotes and no travel surprises inside
            the service area.
          </p>
          <p className="leading-relaxed text-slate-600 sm:text-lg">
            One call to {SITE.phone} (or an email to {SITE.email}) reaches every crew in {region.name} — most quotes
            arrive within one business hour.
          </p>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="region-cities">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>Communities in {region.name}</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Where we clean in {region.name}
            </h2></Rise>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cities.map((c, i) => (
              <Rise key={c.slug} delay={(i % 3) * 0.04}>
                <Link to={paths.city(province.slug, c.slug)} data-testid={`region-city-${c.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-[#FAFAFC] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40">
                  <MapPin className="h-4 w-4 text-cyan-700" />
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-900">{c.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">Population {c.pop} · 12 services available</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700">
                    Cleaning services in {c.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="region-services">
        <Rise><Eyebrow>Services in {region.name}</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Every service, across {region.name}
          </h2></Rise>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <ArrowCard key={s.slug} to={paths.serviceCity(province.slug, lead.slug, s.slug)}
              icon={s.icon} title={`${s.short} in ${region.name}`} desc={s.tagline}
              testid={`region-service-${s.slug}`} />
          ))}
        </div>
      </section>

      {siblings.length > 0 && (
        <section className="border-t border-slate-200 bg-white" data-testid="region-nearby">
          <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
            <Rise><Eyebrow>More regions in {province.name}</Eyebrow></Rise>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
              {siblings.map((r) => (
                <Link key={r.slug} to={`/regions/${r.slug}`} data-testid={`region-nearby-${r.slug}`}
                  className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
                  Cleaning services in {r.name} <ArrowRight className="h-3 w-3 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="region-faq-section">
        <Rise><Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {region.name} cleaning — common questions
          </h2></Rise>
        <div className="mt-8 max-w-3xl"><FAQAccordion faqs={faqs} testid="region-faq" /></div>
      </section>

      <CTABand title={`Book a cleaning anywhere in ${region.name}`}
        lead={`One call reaches every crew in the region. Flat-rate quotes, usually within one business hour.`}
        prefix="region-cta" />
    </div>
  );
}
